import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatINR } from "../lib/api";

export default function AdminCustomers() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [sort, setSort] = useState("createdAt");
    const [detail, setDetail] = useState(null);
    const [adjustOpen, setAdjustOpen] = useState(null); // {kind:'coins'|'points', user}
    const [adjustForm, setAdjustForm] = useState({ delta: 0, reason: "" });

    const refresh = () =>
        api.get("/admin/customers", { params: { q, sort, limit: 50 } })
            .then(({ data }) => { setItems(data.items || []); setTotal(data.total || 0); })
            .catch(() => { setItems([]); setTotal(0); });

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [sort]);

    const openDetail = async (u) => {
        try {
            const { data } = await api.get(`/admin/customers/${u.id}`);
            setDetail(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load customer"); }
    };

    const saveAdjust = async (e) => {
        e.preventDefault();
        if (!adjustOpen) return;
        const path = adjustOpen.kind === "coins" ? "adjust-coins" : "adjust-points";
        try {
            await api.post(`/admin/customers/${adjustOpen.user.id}/${path}`, { delta: Number(adjustForm.delta) || 0, reason: adjustForm.reason });
            toast.success(`Adjusted ${adjustOpen.kind}`);
            setAdjustOpen(null);
            setAdjustForm({ delta: 0, reason: "" });
            refresh();
            if (detail?.user?.id === adjustOpen.user.id) openDetail(adjustOpen.user);
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed"); }
    };

    return (
        <div data-testid="admin-customers-page">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                    <span className="label-eyebrow">Loyalty</span>
                    <h1 className="mt-2 font-display text-4xl text-inkDark">Customers</h1>
                    <p className="mt-1 text-inkMuted">{total} customers · sort by {sort}</p>
                </div>
                <div className="flex items-center gap-2">
                    <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && refresh()} placeholder="Search email…" className="rounded-full border border-inkDark/10 bg-ivory px-4 py-2 text-sm" data-testid="customers-search-input" />
                    <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-full border border-inkDark/10 bg-ivory px-4 py-2 text-sm" data-testid="customers-sort-select">
                        <option value="createdAt">Joined</option>
                        <option value="lifetimeSpendPaise">Lifetime spend</option>
                        <option value="stylePoints">Points</option>
                        <option value="styleCoins">Coins</option>
                    </select>
                    <button onClick={refresh} className="btn-pill text-sm">Refresh</button>
                </div>
            </div>

            <div className="mt-6 overflow-x-auto bg-ivory rounded-2xl border border-inkDark/5">
                <table className="w-full text-sm">
                    <thead className="bg-beige text-inkMuted">
                        <tr>
                            <th className="text-left px-4 py-3">Email</th>
                            <th className="text-left px-4 py-3">Name</th>
                            <th className="text-left px-4 py-3">Tier</th>
                            <th className="text-right px-4 py-3">Lifetime</th>
                            <th className="text-right px-4 py-3">Points</th>
                            <th className="text-right px-4 py-3">Coins</th>
                            <th className="text-left px-4 py-3">Ref</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((u) => (
                            <tr key={u.id} className="border-t border-inkDark/5" data-testid={`customer-row-${u.id}`}>
                                <td className="px-4 py-3 truncate max-w-[16rem]">{u.email}</td>
                                <td className="px-4 py-3">{u.name}</td>
                                <td className="px-4 py-3 capitalize">{u.tier || "muse"}</td>
                                <td className="px-4 py-3 text-right">{formatINR((u.lifetimeSpendPaise || 0) / 100)}</td>
                                <td className="px-4 py-3 text-right">{u.stylePoints || 0}</td>
                                <td className="px-4 py-3 text-right">{u.styleCoins || 0}</td>
                                <td className="px-4 py-3 font-mono text-xs">{u.referralCode || "—"}</td>
                                <td className="px-4 py-3 text-right"><button onClick={() => openDetail(u)} className="text-skyblueDeep text-xs underline">View</button></td>
                            </tr>
                        ))}
                        {items.length === 0 ? <tr><td colSpan={8} className="px-4 py-10 text-center text-inkMuted">No customers yet.</td></tr> : null}
                    </tbody>
                </table>
            </div>

            {detail ? (
                <div className="fixed inset-0 bg-inkDark/50 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
                    <div className="bg-ivory rounded-2xl shadow-lift w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6 border-b border-inkDark/5 flex items-start justify-between gap-4">
                            <div>
                                <span className="label-eyebrow">Customer</span>
                                <h2 className="font-display text-2xl">{detail.user.name || detail.user.email}</h2>
                                <p className="text-sm text-inkMuted">{detail.user.email} · tier <span className="capitalize">{detail.user.tier}</span> · ref <span className="font-mono">{detail.user.referralCode}</span></p>
                            </div>
                            <div className="flex gap-2">
                                <button className="btn-pill text-xs" onClick={() => { setAdjustOpen({ kind: "coins", user: detail.user }); setAdjustForm({ delta: 0, reason: "" }); }} data-testid="adjust-coins-btn">Adjust coins</button>
                                <button className="btn-pill text-xs" onClick={() => { setAdjustOpen({ kind: "points", user: detail.user }); setAdjustForm({ delta: 0, reason: "" }); }} data-testid="adjust-points-btn">Adjust points</button>
                            </div>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Stat label="Lifetime spend" value={formatINR((detail.user.lifetimeSpendPaise || 0) / 100)} />
                            <Stat label="Style Points" value={detail.user.stylePoints || 0} />
                            <Stat label="Style Coins" value={detail.user.styleCoins || 0} />
                        </div>
                        <div className="px-6 pb-6">
                            <div className="label-eyebrow mt-4 mb-2">Orders ({detail.orders.length})</div>
                            <div className="space-y-2 text-sm">
                                {detail.orders.slice(0, 8).map((o) => (
                                    <div key={o.id} className="flex items-center justify-between gap-3 bg-beige rounded-xl px-3 py-2">
                                        <span className="truncate">{o.id.slice(0, 8)}… · <span className="capitalize">{o.paymentMethod || "prepaid"}</span> · {o.fulfillmentStatus}</span>
                                        <span className="text-inkMuted">{formatINR(o.grandTotal)}</span>
                                    </div>
                                ))}
                                {detail.orders.length === 0 ? <div className="text-inkMuted text-xs">No orders yet.</div> : null}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
                                <div>
                                    <div className="label-eyebrow mb-2">Points history</div>
                                    <div className="space-y-1 text-xs">
                                        {detail.pointsHistory.slice(0, 12).map((e) => (
                                            <div key={e.id} className="flex justify-between"><span>{e.type}</span><span>{e.delta > 0 ? `+${e.delta}` : e.delta}</span></div>
                                        ))}
                                        {detail.pointsHistory.length === 0 ? <div className="text-inkMuted">—</div> : null}
                                    </div>
                                </div>
                                <div>
                                    <div className="label-eyebrow mb-2">Coins history</div>
                                    <div className="space-y-1 text-xs">
                                        {detail.coinsHistory.slice(0, 12).map((e) => (
                                            <div key={e.id} className="flex justify-between"><span>{e.type}</span><span>{e.delta > 0 ? `+${e.delta}` : e.delta}</span></div>
                                        ))}
                                        {detail.coinsHistory.length === 0 ? <div className="text-inkMuted">—</div> : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {adjustOpen ? (
                <div className="fixed inset-0 bg-inkDark/60 z-[60] flex items-center justify-center p-4" onClick={() => setAdjustOpen(null)}>
                    <form onSubmit={saveAdjust} onClick={(e) => e.stopPropagation()} className="bg-ivory rounded-2xl shadow-lift w-full max-w-md p-6 space-y-4">
                        <h3 className="font-display text-xl">Adjust {adjustOpen.kind} — {adjustOpen.user.email}</h3>
                        <label className="block">
                            <span className="label-eyebrow">Delta (can be negative)</span>
                            <input type="number" value={adjustForm.delta} onChange={(e) => setAdjustForm({ ...adjustForm, delta: e.target.value })} className="mt-2 w-full rounded-xl border border-inkDark/10 px-4 py-2.5" required data-testid="adjust-delta-input" />
                        </label>
                        <label className="block">
                            <span className="label-eyebrow">Reason</span>
                            <input value={adjustForm.reason} onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })} className="mt-2 w-full rounded-xl border border-inkDark/10 px-4 py-2.5" placeholder="e.g. Goodwill credit" data-testid="adjust-reason-input" />
                        </label>
                        <div className="flex justify-end gap-3">
                            <button type="button" onClick={() => setAdjustOpen(null)} className="btn-ghost text-sm">Cancel</button>
                            <button type="submit" className="btn-pill text-sm" data-testid="adjust-save-btn">Save</button>
                        </div>
                    </form>
                </div>
            ) : null}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-beige rounded-2xl p-4">
            <div className="label-eyebrow text-[10px]">{label}</div>
            <div className="font-display text-2xl mt-1">{value}</div>
        </div>
    );
}
